import { useMemo, type RefObject } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { BodyRegion, LivingState } from './lifeOmega'
import type { StrikeContact } from './targetedCombat'

type BoneMap=Record<BodyRegion,THREE.Bone[]>
const emptyMap=():BoneMap=>({'head':[],'torso':[],'left-arm':[],'right-arm':[],'left-leg':[],'right-leg':[]})
const normalize=(name:string)=>name.toLowerCase().replace(/[^a-z0-9]/g,'')

function semanticRegion(name:string):BodyRegion|null{
 const n=normalize(name)
 if(/head|neck/.test(n))return'head'
 if(/left.*(upperarm|forearm|hand)|mixamorigleft(arm|forearm|hand)|upperarml|forearml|handl/.test(n))return'left-arm'
 if(/right.*(upperarm|forearm|hand)|mixamorigright(arm|forearm|hand)|upperarmr|forearmr|handr/.test(n))return'right-arm'
 if(/left.*(upleg|thigh|leg|calf|foot)|mixamorigleft(upleg|leg|foot)|thighl|calfl|footl/.test(n))return'left-leg'
 if(/right.*(upleg|thigh|leg|calf|foot)|mixamorigright(upleg|leg|foot)|thighr|calfr|footr/.test(n))return'right-leg'
 if(/spine|chest|hips|pelvis|torso/.test(n))return'torso'
 return null
}

export function discoverReactionBones(model:THREE.Object3D){
 const map=emptyMap()
 model.traverse(node=>{
  if(!(node instanceof THREE.Bone))return
  const region=semanticRegion(node.name)
  if(region&&map[region].length<4)map[region].push(node)
 })
 return map
}

const clamp01=(value:number)=>Math.max(0,Math.min(1,value))
const reflexStrength=(life:LivingState)=>clamp01(life.reflexTimer/.28)
const painStrength=(life:LivingState,region:BodyRegion)=>clamp01(life.regionalPain[region]*.85+life.pain*.15)

export function useLocalizedReactionRig(
 model:THREE.Object3D,
 _life:LivingState,
 _contact:StrikeContact|undefined,
 _enabled:boolean,
){
 // The animation mixer owns bone transforms. Mutating bone.rotation with += inside
 // useFrame accumulates offsets across frames and can visibly deform skinned meshes.
 // Keep semantic bone discovery available for diagnostics, but route hit reaction
 // through useLocalizedReactionRoot where offsets are damped and non-destructive.
 return useMemo(()=>discoverReactionBones(model),[model])
}
export function useLocalizedReactionRoot(
 root:RefObject<THREE.Group>,
 life:LivingState,
 contact:StrikeContact|undefined,
){
 useFrame((_,dt)=>{
  const group=root.current
  if(!group)return
  const region=life.lastRegion,pain=painStrength(life,region),reflex=reflexStrength(life)
  const strength=clamp01(pain*.42+reflex*.72)
  const direction=contact?.forceX??1
  let targetX=0,targetZ=0,targetY=0
  if(region==='head')targetZ=-direction*.035*strength
  else if(region==='torso'){targetX=.07*strength;targetZ=-direction*.045*strength}
  else if(region==='left-arm'||region==='right-arm')targetZ=-direction*.025*strength
  else{targetZ=(region==='left-leg'?-1:1)*direction*.06*strength;targetY=-.025*strength}
  if(life.balance<.55)targetZ+=direction*(.55-life.balance)*.08
  group.rotation.x=THREE.MathUtils.damp(group.rotation.x,targetX,14,dt)
  group.rotation.z=THREE.MathUtils.damp(group.rotation.z,targetZ,16,dt)
  group.position.y=THREE.MathUtils.damp(group.position.y,targetY,12,dt)
 })
}
